import { useCallback } from 'react';
import { queryOptions, skipToken, useQueries, useQuery, UseQueryResult } from '@tanstack/react-query';
import { LogisticsGroundTrip } from '@/api/apis/drive-service';
import { driveServiceTripsQueryKeys } from './drive-service.keys';
import { getGroundTripsList } from './drive-service.fetcher';

type QueryResultType<T> = {
  data: T;
  isLoading: boolean;
};

export const driveServiceQuery = {
  list: (tripIds: string[]) =>
    queryOptions({
      queryKey: driveServiceTripsQueryKeys.getList(tripIds),
      queryFn: tripIds.length ? () => getGroundTripsList(tripIds) : skipToken,
      staleTime: Infinity,
      enabled: Boolean(tripIds.length),
    }),
  item: (tripId?: string | null) =>
    queryOptions<LogisticsGroundTrip>({
      queryKey: driveServiceTripsQueryKeys.getItem(tripId),
      queryFn: skipToken,
      staleTime: Infinity,
      enabled: Boolean(tripId),
      select: (data) => {
        const isMergedTrip = !!data.linkedLogisticsLegs && data.linkedLogisticsLegs.legs.length > 1;

        return {
          ...data,
          isMergedTrip,
        };
      },
    }),
};

export const useDriveTripsByIdsQuery = (tripIds: string[]) => {
  const tripIdsSet = new Set(tripIds);
  return useQueries<LogisticsGroundTrip[], QueryResultType<LogisticsGroundTrip>[]>({
    queries: Array.from(tripIdsSet.values()).map((id) => ({
      queryKey: driveServiceTripsQueryKeys.getItem(id),
      queryFn: skipToken,
    })),
    combine: useCallback(
      (result: UseQueryResult[]) =>
        result.map((r) => ({ data: r.data, isLoading: r.isPending })) as QueryResultType<LogisticsGroundTrip>[],
      [],
    ),
  });
};

export const useDriveTripQuery = (tripId?: string | null) => {
  return useQuery(driveServiceQuery.item(tripId));
};
